import { Box } from '@mui/material';
import { Outlet ,useLocation} from 'react-router-dom';
import SideBar from './sidebar';
import Header from './header';
import { useCallback, useEffect ,useState } from 'react';
import { colors } from 'styles/colors';
import RightSidePanel from './rightSidePanel';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { RootState } from 'store';
import { initializeSignalR } from 'services/SignalRService';
import { getFeatureFlag , axiosGetTrendingUsers } from 'services/api';
import { setFeatureFlag } from 'store/featureFlagSlice';
import { clearGuestAction } from 'store/guestModeSlice';
import { appAccountValidation, swmAccountValidation } from 'utilities/authTokenValidation';
import ChatFriendsList from 'pages/modules/chats/chatsScreen';
import ActiveUsers from 'pages/modules/home/activeUsersList';
import { ITrendingUser } from 'models/apiPaths';
import { isOAuthFlowActive } from 'utilities/oauthBridge';
import HomeTabs from 'pages/modules/home/HomeTabs';

const Layout = () => {
  const guestMode = useAppSelector((state: RootState) => state.guestMode);
  const dispatch = useAppDispatch();
  const { isLoggedIn, userProfile, authToken }: any = useAppSelector(
    (state: RootState) => state.userAuth
  );
  const { isChatEnabled }: any = useAppSelector(
    (state: RootState) => state.featureFlag
  );
  const { blockedUserIds }: any = useAppSelector(
    (state: RootState) => state.utilsData
  );
  const isAppAccount = appAccountValidation?.(authToken ?? null);
  const isSWMAccount = swmAccountValidation?.(authToken ?? null);
  const [activeUsersList, setActiveUserList] = useState<ITrendingUser[]>([]);
  const [homeTab, setHomeTab] = useState<'mainStream' | 'following'>('mainStream');
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x)[0];

  const isHomeFeed = pathnames === 'home' || !pathnames;

  const fetchAndSetFeatureFlag = useCallback(async () => {
    if (isOAuthFlowActive()) {
      return;
    }
    try {
      if (authToken) {
        const response = await getFeatureFlag();
        dispatch(setFeatureFlag({ isChatEnabled: true }));

        if (response.chatEnabled && userProfile?.id && !(isAppAccount || isSWMAccount)) {
          dispatch(initializeSignalR(userProfile?.id));
        }
      }
    } catch (error: any) {
      console.error('Feature flag API call failed:', error.response?.data?.message || error.message);
    }
  }, [authToken, dispatch, userProfile?.id]);

  const checkAndClearGuestModeData = useCallback(async () => {
    const actionList = ['pollVote', 'votePost', 'sharePost', 'rePost'];
    if (guestMode?.action && !actionList.includes(guestMode?.action)) {
      dispatch(clearGuestAction());
    }
  }, [dispatch, guestMode?.action]);

  const activeUserList = useCallback(async () => {
    if (isOAuthFlowActive()) {
      return;
    }
    await axiosGetTrendingUsers()
      .then((data: ITrendingUser[]) => {
        setActiveUserList(data);
      })
      .catch((error: any) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    fetchAndSetFeatureFlag();
    checkAndClearGuestModeData();
  }, [fetchAndSetFeatureFlag, checkAndClearGuestModeData]);

  useEffect(() => {
    if (isHomeFeed) {
      activeUserList();
    }
  }, [isHomeFeed, authToken, blockedUserIds, activeUserList]);
  

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          background: colors.background.white,
          height: '100%',
          width: '100%',
          margin: '0 auto',
          gap: 0,
          overflowX: 'hidden'
        }}
        maxWidth={'xl'}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: '100%', md: '25%' },
            flexShrink: 0,
            boxSizing: 'border-box',
            height: { xs: '100%', md: '100vh' },
            position: { xs: 'relative', md: 'sticky' },
            top: 0
          }}
        >
          <SideBar isLoggedIn={isLoggedIn}/>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            flexShrink: 0,
            minWidth: 0,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',       
            overflowX: 'hidden',
            borderLeft: `1px solid ${colors.border.primary}`,
            borderRight: `1px solid ${colors.border.primary}`
          }}
        >
          <Box
            sx={{
              borderBottom: `1px solid ${colors.border.primary}`,
              height: 70,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: {xs: 1.5, sm: 3},
              zIndex: 999,
              background: colors.background.white
            }}
          >
            <Header />
          </Box>

          <Box
            id='scrollToElement'
            sx={{
              flex: 1,         
              minHeight: 0,    
              overflowY: 'scroll',
              overflowX: 'hidden',
              background: isHomeFeed ? colors.background.feedBackground : colors.background.white
            }}
          >
            {isHomeFeed && (
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  flexShrink: 0,
                  pt: 1,
                  zIndex: 10,
                  backgroundColor: colors.background.white,
                  maxWidth: '100%'
                }}
              >
                <ActiveUsers activeUsersList={activeUsersList} isLoggedIn={isLoggedIn} />
                {isLoggedIn && !(isAppAccount || isSWMAccount) && (
                  <HomeTabs activeTab={homeTab} onTabChange={setHomeTab} />
                )}
              </Box>
            )}
            <Outlet context={{ homeTab, setHomeTab }} />
          </Box>
        </Box>
        <Box
          sx={{
            display: { xs: 'none', sm:'none', md:'flex'},
            width: { xs: '100%', sm: '30%', md: '25%' },
            flexShrink: 0,
            boxSizing: 'border-box',
            height: '100vh',
            px: 2
          }}
        >
          <RightSidePanel isLoggedIn={isLoggedIn}/>
        </Box>
      </Box>
        {isLoggedIn && 
    isChatEnabled && 
    pathnames !== 'chat' && 
    !(isAppAccount || isSWMAccount) && (
      <Box
        sx={{
          position: 'fixed', bottom: 0, right: { md: 20, lg: 40 },width: { md: 280, lg: 320 },maxHeight: '70vh',
          background: colors.background.approxAliceBlue, borderRadius: '8px 8px 0 0', zIndex: 1000, display: { xs: 'none', md: 'flex' },
          flexDirection: 'column', overflow: 'hidden', border: `1px solid ${colors.border.primary}`, borderBottom: 'none'
        }}
      >
        <ChatFriendsList />
      </Box>
    )}
    </>
  );
};

export default Layout;
